/** requestIdleCallback's deadline: a busy main thread still gets the task within this. */
export const IDLE_TIMEOUT_MS = 5_000;

/** Where requestIdleCallback is missing (Safari), the task waits this long after `load` instead. */
export const IDLE_FALLBACK_DELAY_MS = 1_000;

/**
 * Runs `task` once, after the window's `load` event and then when the browser is idle. For work nobody waits on
 * (a third-party widget, the outdated-browser banner) that used to compete with the entry chunk, the first route's
 * chunk and the first API calls during startup (UX roadmap #20, #26). Returns a cancel function.
 */
export function afterLoadWhenIdle( task: () => void ): () => void {
    let cancelled = false;
    let cancelPending: () => void = () => {};

    const runWhenIdle = (): void => {
        if ( cancelled ) return;
        if ( 'function' === typeof window.requestIdleCallback ) {
            const handle = window.requestIdleCallback( () => { if ( !cancelled ) task(); }, { timeout: IDLE_TIMEOUT_MS } );
            cancelPending = () => window.cancelIdleCallback( handle );
        } else {
            const handle = window.setTimeout( () => { if ( !cancelled ) task(); }, IDLE_FALLBACK_DELAY_MS );
            cancelPending = () => window.clearTimeout( handle );
        }
    };

    if ( 'complete' === document.readyState ) {
        runWhenIdle();
    } else {
        window.addEventListener( 'load', runWhenIdle, { once: true } );
        cancelPending = () => window.removeEventListener( 'load', runWhenIdle );
    }

    return () => {
        cancelled = true;
        cancelPending();
    };
}
